import axios from 'axios';
import axiosRetry from 'axios-retry';
import store from '../../store';
import { actions as authActions } from '../../authentication/slice';
import {
	REQUEST_HEADER_KEYS,
	RESPONSE_HEADER_KEYS,
} from '../constants/apiHeaders';

const buildAuthHeaders = () => {
	const authState = store.getState()?.auth || {};
	const headers = {};

	if (authState.accessToken) {
		headers.Authorization = `Bearer ${authState.accessToken}`;
	}

	const effectiveUserSecret = authState.isImpersonating
		? authState.impersonatedUser?.userSecret
		: authState.authenticatedUser?.userSecret;

	if (authState.isImpersonating && effectiveUserSecret) {
		headers[REQUEST_HEADER_KEYS.xImpersonated] = effectiveUserSecret;
	}

	return headers;
};

/**
 * Creates a configured Axios instance with retry logic.
 * This is a low-level transport factory used by useApiClient.
 * Feature components should use specialized API hooks rather than importing this directly.
 *
 * This factory function returns a new Axios client pre-configured with:
 * - Custom baseURL and timeout (with sensible defaults)
 * - Automatic retry on network failures and idempotent requests
 * - Skips retries for cancelled/aborted requests
 *
 * @param {Object} [options] - Configuration options
 * @param {string} [options.baseURL] - Base URL for the API (defaults to REACT_APP_BASE_API_URL)
 * @param {number} [options.timeout] - Request timeout in milliseconds (defaults to 15000)
 * @param {number} [options.retries] - Number of retry attempts (defaults to 3)
 *
 * @returns {import('axios').AxiosInstance} Configured Axios instance
 */
export function createApiClient(options = {}) {
	const client = axios.create({
		baseURL: options.baseURL ?? process.env.REACT_APP_BASE_API_URL,
		timeout: options.timeout ?? 15000,
	});

	client.interceptors.request.use((config) => {
		const authHeaders = buildAuthHeaders();
		return {
			...config,
			headers: {
				...authHeaders,
				...config.headers,
			},
		};
	});

	client.interceptors.response.use((response) => {
		const userSecret = response?.headers?.[RESPONSE_HEADER_KEYS.xImpersonated];
		if (userSecret) {
			store.dispatch(authActions.updateSecret(userSecret));
		}
		return response;
	});

	const retries = options.retries ?? 3;

	axiosRetry(client, {
		retries,
		retryCondition: (error) => {
			if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
				return false;
			}
			return axiosRetry.isNetworkOrIdempotentRequestError(error);
		},
	});

	return client;
}
